import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getReimbursmentInfo, patchReimbursementInfo } from '../../../../../store/slices/Citizenship/adminSlice';
import CitizenshipFeeField from './CitizenshipFeeField';
import feeFieldsConfig from '../../../../../config/feeFieldsConfig';
import Notifications from '../../../../../components/common/Notifications';
import { trackButtonClick, trackFormSave } from '../../../../../services/analytics';

interface ReimbursmentTabProps {
  applicationData?: any;
  applicationId?: string;
}

const ReimbursmentTab = ({ applicationData, applicationId }: ReimbursmentTabProps) => {
  const dispatch = useDispatch();
  const { reimbursements, reimbursementError } = useSelector((state: any) => state.admin);
  const [actuals, setActuals] = useState<Record<string, string>>({});
  const [readies, setReadies] = useState<Record<string, boolean>>({});
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);

  // Helper function to get fee data from API response by feeType
  const getFeeDataByType = (feeType: string) => {
    return reimbursements?.reimbursements?.find((item: any) => item.feeType === feeType) || null;
  };

  // Sync state with API response data
  useEffect(() => {
    if (reimbursements?.reimbursements && Array.isArray(reimbursements.reimbursements)) {
      const newActuals: Record<string, string> = {};
      const newReadies: Record<string, boolean> = {};

      feeFieldsConfig.forEach((config) => {
        if (config.feeType) {
          const feeData = getFeeDataByType(config.feeType);
          if (feeData) {
            // Show whatever value comes from API (including 0)
            newActuals[config.id] = feeData.actualAmount != null ? String(feeData.actualAmount) : '';
            // Show ready status as per API response
            newReadies[config.id] = feeData.readyForPayment === 'Y' || feeData.readyForPayment === true;
          } else {
            newActuals[config.id] = '';
            newReadies[config.id] = false;
          }
        } else if (config.isCommentField) {
          // Map comment value to actuals
          newActuals[config.id] = reimbursements?.comment ?? '';
        }
      });

      setActuals(newActuals);
      setReadies(newReadies);
    }

  
  }, [reimbursements]);

  useEffect(() => {
    if (applicationId) {
      dispatch(getReimbursmentInfo({ applicationId }) as any);
    }
  }, [applicationId, dispatch]);

  // Helper function to check if any fee is editable (paymentDate is null)
  const hasEditableFees = () => {
    return feeFieldsConfig.some((cfg) => {
      if (!cfg.feeType || cfg.isCommentField) return false;
      const feeData = getFeeDataByType(cfg.feeType);
      return !feeData?.paymentDate; // Return true if paymentDate is null
    });
  };

  const handleSave = () => {
    // Prepare form data with actual fee handling
    const updatedActuals = { ...actuals };
    const updatedReadies = { ...readies };

    // Process all fee fields - set empty fees to 0
    const feeFieldIds = ['filling', 'biometric', 'totalClass', 'AdjustedFee'];
    feeFieldIds.forEach((fieldId) => {
      // If any fee is empty, set to 0
      if (!updatedActuals[fieldId] || updatedActuals[fieldId].trim() === '') {
        updatedActuals[fieldId] = '0';
        // If actual fee is 0, uncheck ready for payment
        updatedReadies[fieldId] = false;
      } else if (updatedActuals[fieldId] === '0') {
        // If actual fee is 0, also uncheck ready for payment
        updatedReadies[fieldId] = false;
      }
    });

    const nuid = applicationData?.nuid || '';
    const formData = {
      fees: feeFieldsConfig
        .filter(cfg => cfg.feeType) // Only include items with feeType
        .map((cfg) => ({
          feeType: cfg.feeType,
          actualAmount: updatedActuals[cfg.id] ? Number(updatedActuals[cfg.id]) : 0,
          readyForPayment: updatedReadies[cfg.id] ? 'Y' : 'N',
        })),
    };
    dispatch(patchReimbursementInfo({ applicationId: applicationId || '', data: formData, nuid }) as any).then(
      (result: any) => {
        if (result.payload) {
          // Update UI state with saved values
          setActuals(updatedActuals);
          setReadies(updatedReadies);
          
          // Track successful save
          console.log(`📊 Analytics: Reimbursement info saved - Application ${applicationId}`);
          trackButtonClick('Save', 'citizenship-reimbursement-tab', { applicationId });
          trackFormSave('citizenship', undefined, { applicationId, feesCount: formData.fees.length });
          
          // Show success toast
          setShowSuccessToast(true);
          setTimeout(() => setShowSuccessToast(false), 20000);
          
          // Refetch reimbursement data after successful patch
          dispatch(getReimbursmentInfo({ applicationId: applicationId || '' }) as any);
        }
      }
    );
  };

  // If there's an error, show centered notification instead of form
  if (reimbursementError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Notifications
          messageInfo={typeof reimbursementError === 'string' ? reimbursementError : 'An error occurred while loading reimbursement information.'}
          status={false}
        />
      </div>
    );
  }

  return (
    <div>
      {showSuccessToast && (
        <Notifications
          messageInfo="The selected citizen reimbursment application fees has been saved."
          status={true}
        />
      )}
      <div className="flex flex-col gap-6">
        {feeFieldsConfig.map((cfg) => {
          // Get fee data from API response using feeType
          const feeData = cfg.feeType ? getFeeDataByType(cfg.feeType) : null;
          const estimatedAmount = feeData?.estimatedAmount !== undefined && feeData?.estimatedAmount !== null ? String(feeData.estimatedAmount) : '';
          const updatedBy = feeData?.lastUpdatedBy ?? '';
          const updatedDate = feeData?.lastUpdatedDate ?? '';
          // Disable fields if paymentDate is NOT null (fee already paid - locked row)
          // Enable fields if paymentDate is null (editable row)
          const isDisabled = !!feeData?.paymentDate;

          return (
            <CitizenshipFeeField
              key={cfg.id}
              fieldId={cfg.id}
              title={cfg.title}
              estimated={estimatedAmount}
              actual={actuals[cfg.id] || ''}
              onActualChange={(v) => {
                // Update actual amount
                setActuals((p) => ({ ...p, [cfg.id]: v }));
                // If the value is cleared, also uncheck the corresponding checkbox
                if (!v || v.trim() === '') {
                  setReadies((p) => ({ ...p, [cfg.id]: false }));
                }
              }}
              readyForPayment={readies[cfg.id] || false}
              onReadyChange={(c) => setReadies((p) => ({ ...p, [cfg.id]: c }))}
              updatedBy={updatedBy}
              updatedDate={updatedDate}
              showUpdatedInfo={true}
              hasEstimated={estimatedAmount !== ''}
              isCommentField={cfg.isCommentField}
              isDisabled={isDisabled}
            />
          );
        })}

        <div className="mt-8 flex justify-start gap-4">
          <button
            type="button"
            className="button -primary -light"
            onClick={handleSave}
            disabled={!hasEditableFees()}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReimbursmentTab;
